/**
 * Thumbnail utilities for storage videos
 * Handles extracting and selecting thumbnails from video processing job payloads
 */

export interface ThumbnailAsset {
  timestampMs: number;
  publicUrl: string;
  width?: number;
  height?: number;
}

export interface ThumbnailPayload {
  generatedAssets?: ThumbnailAsset[];
  videoUuid?: string;
  orgId?: string;
}

/**
 * Parse the payload from a video processing job
 * The payload is stored as a JSON string in the database
 */
export function parseThumbnailPayload(payload: string | Record<string, any>): ThumbnailPayload | null {
  try {
    if (typeof payload === 'string') {
      return JSON.parse(payload);
    }
    return payload as ThumbnailPayload;
  } catch (error) {
    console.warn('Error parsing thumbnail payload:', error);
    return null;
  }
}

/**
 * Get all thumbnail assets from a parsed payload
 */
export function getThumbnailAssets(payload: ThumbnailPayload | null): ThumbnailAsset[] {
  if (!payload?.generatedAssets || !Array.isArray(payload.generatedAssets)) {
    return [];
  }
  return payload.generatedAssets;
}

/**
 * Find the closest thumbnail to a given timestamp (in seconds)
 * Returns the thumbnail URL or null if no thumbnails available
 */
export function getClosestThumbnail(
  assets: ThumbnailAsset[],
  videoTimeSeconds: number
): string | null {
  if (!assets.length) return null;
  
  const targetMs = videoTimeSeconds * 1000;
  
  // Find the closest thumbnail by timestamp
  let closest = assets[0];
  let minDiff = Math.abs(assets[0].timestampMs - targetMs);
  
  for (const asset of assets) {
    const diff = Math.abs(asset.timestampMs - targetMs);
    if (diff < minDiff) {
      minDiff = diff;
      closest = asset;
    }
  }
  
  return closest.publicUrl;
}

/**
 * Get the first (default) thumbnail - typically at 0ms
 * Useful for match listing thumbnails
 */
export function getDefaultThumbnail(assets: ThumbnailAsset[]): string | null {
  if (!assets.length) return null;
  
  // Prefer the 0ms thumbnail, otherwise use the first one
  const zeroThumbnail = assets.find(a => a.timestampMs === 0);
  return zeroThumbnail?.publicUrl || assets[0].publicUrl;
}

/**
 * Build a map of video_id -> thumbnail assets for efficient lookup
 */
export function buildThumbnailMap(
  jobs: Array<{ video_id: string; payload: string | Record<string, any> }>
): Map<string, ThumbnailAsset[]> {
  const map = new Map<string, ThumbnailAsset[]>();
  
  for (const job of jobs) {
    const parsed = parseThumbnailPayload(job.payload);
    const assets = getThumbnailAssets(parsed);
    if (assets.length > 0) {
      map.set(job.video_id, assets);
    }
  }
  
  return map;
}

// Use environment variable for CDN URL, fallback to default for backward compatibility
const CDN_BASE_URL = process.env.REACT_APP_R2_PUBLIC_URL || 'https://cdn.capture.kridah.com';

/**
 * Generates a thumbnail URL directly from the video's storage path or public URL.
 * Pattern: {CDN_BASE}/{org_id}/{video_uuid}/thumbnails/{timestampMs}.webp
 * 
 * This bypasses the need to query video_processing_jobs table.
 * 
 * @param videoUrl - The video's public_url or storage_path
 * @param timestampSeconds - The timestamp in seconds (will be rounded to nearest 60s)
 */
export function generateStorageThumbnailUrl(
  videoUrl: string,
  timestampSeconds: number = 0
): string | null {
  try {
    // Extract the path: org_xxx/uuid/master.mp4
    let storagePath = videoUrl;
    if (videoUrl.startsWith('http')) {
      const url = new URL(videoUrl);
      storagePath = url.pathname.replace(/^\//, ''); // Remove leading slash
    }

    // Pattern: org_xxx/uuid/master.mp4 -> org_xxx/uuid
    const pathParts = storagePath.split('/');
    if (pathParts.length < 2) return null;

    // Remove the filename (master.mp4)
    pathParts.pop();
    const basePath = pathParts.join('/');

    // Round timestamp to nearest 60 seconds (thumbnails are generated every 60s)
    const roundedTimestampMs = Math.floor(timestampSeconds / 60) * 60 * 1000;

    return `${CDN_BASE_URL}/${basePath}/thumbnails/${roundedTimestampMs}.webp`;
  } catch (error) {
    console.error('Error generating storage thumbnail URL:', error);
    return null;
  }
}

